import {
    ACTIVE_SCREEN_KEY,
    setScreenData,
    formatMessage,
    setErrorScreenMessage,
    Store,
    removeAllScreenMessages,
    setSuccessfulScreenMessage,
    getScreenData,
    hideDrawer,
    hideDialog,
    getScreenUpdated,
    setInformationalScreenMessage,
    removeScreenUpdated,
    setScreenUpdated,
    removeScreenData,
    setWarningScreenMessage,
} from 'cw-ui-core';
import { all, call, put, fork, cancel, delay } from 'redux-saga/effects';
import { UMS_LOADING_DIALOG } from 'src/features/users/components/UMSLoadingDialog/UMSLoadingDialog';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import size from 'lodash/size';
import get from 'lodash/get';
import find from 'lodash/find';
import some from 'lodash/some';
import every from 'lodash/every';
import { isImmutable } from 'immutable';
import {
    GET,
    POST,
    PATCH,
    DELETE,
    PUT,
    getHeaders,
    getPartnerId,
    autoHideMessages,
    loader,
} from 'src/services/ApiService';
import { runSagaWithUMSLoader, reloadCurrentUsersPage } from 'src/services/UsersService';
import { loadConnectedAppsDetails } from 'src/services/ConnectedAppsService';
import { EDIT_ROLE_DRAWER_ASSIGN_USERS_TAB, EDIT_ROLE_DRAWER } from 'src/features/identity-roles/constants';
import { assignedUsersPath } from 'src/features/identity-roles/components/AssignUsers/ConstantPaths';
import Locale from 'src/i18n';
import { DEFAULT_ROLES } from 'src/constants';

export const DefaultRoles = Object.freeze([DEFAULT_ROLES.Admin, DEFAULT_ROLES.BillingAdmin]);

const sortedBy = {
    Admin: 0,
    'Billing Admin': 1,
};

const sortRoles = roles => {
    let defaultRoles = roles.filter(role => DefaultRoles.includes(role.name));
    defaultRoles = defaultRoles.sort((a, b) => sortedBy[a.name] - sortedBy[b.name]);
    const remainingRoles = roles.filter(role => !DefaultRoles.includes(role.name));
    return [...defaultRoles, ...remainingRoles];
};

export function* loadRoles(screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isRolesReady'], false));
    const result = yield call(GET, '/roles?includeUsersCount=true', getHeaders());
    if (result.isSuccess) {
        let roles = result.response;
        roles = sortRoles(roles);

        yield put(setScreenData(['roles'], roles, screenKey));
        yield put(setScreenData(['isRolesReady'], true));
    } else {
        const error = formatMessage(Locale.failedGetRoles);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield put(setScreenData(['isRolesReady'], true));
    }
}

export function* getRole(id, screenKey = ACTIVE_SCREEN_KEY) {
    const result = yield call(GET, `/roles/${id}`, getHeaders());
    if (result.isSuccess) {
        yield put(setScreenData(['roleDetails'], result.response, screenKey));
        yield put(setScreenData(['savedRole', 'roleDetails'], result.response, screenKey));
    } else {
        const error = formatMessage(Locale.failedGetRole);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
    }

    yield put(setScreenData(['isAssignedUsersReady'], false, screenKey));
    const resultUsers = yield call(GET, `/roles/${id}/users`, getHeaders());
    if (resultUsers.isSuccess) {
        const users = resultUsers.response.map(user => user.id) ?? [];
        yield put(setScreenData(['checkboxes', 'selectedUsers'], users, screenKey));
        yield put(setScreenData(assignedUsersPath, users, screenKey));
        yield put(setScreenData(['savedRole', 'checkboxes', 'selectedUsers'], users, screenKey));
    } else {
        const error = formatMessage(Locale.failedGetRoleUsers);
        yield put(setErrorScreenMessage(error, false, result.errorMessages, screenKey));
    }
    yield put(setScreenData(['isAssignedUsersReady'], true, screenKey));
}

export function* updateRoleUsers(id, users, errorMessage) {
    let usersResult;
    const errors = [];
    const usersPayload = {
        userIds: users,
    };
    if (users) {
        usersResult = yield call(PUT, `/iambff/v1/identity-roles/${id}/users`, getHeaders(), usersPayload);
    }

    if (usersResult && !usersResult.isSuccess) {
        errors.push({
            screenMessage: errorMessage || formatMessage(Locale.failedUpdateRoleUsers),
            errorMessages: usersResult.errorMessages,
        });
    }

    return errors;
}

export function* roleAssignments(id, connectedApps) {
    let result;
    const errors = [];
    if (connectedApps && connectedApps.length > 0) {
        const body = {
            identityRoleId: id,
            connectedApps,
        };
        result = yield call(POST, `/iambff/v1/identity-roles/${id}/connected-app-mappings`, getHeaders(), body);
    }

    if (result && !result.isSuccess) {
        errors.push({
            screenMessage: formatMessage(Locale.failedAddAccess),
            errorMessages: result.errorMessages,
        });
    }

    return errors;
}

export function* getConnectedAppsMapping(screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isRoleMappingReady'], false));
    const result = yield call(GET, '/iambff/v1/identity-roles/connected-apps', getHeaders());
    if (result.isSuccess) {
        const connectedAppsMappingResponse = result.response;
        const connectedAppsMapping = {};
        forEach(connectedAppsMappingResponse, ({ roleId, connectedApps }) => {
            connectedAppsMapping[roleId] = connectedApps;
        });

        yield put(setScreenData(['connectedAppsMapping'], connectedAppsMapping, screenKey));
        yield put(setScreenData(['isRoleMappingReady'], true));
    } else {
        const error = formatMessage(Locale.failedConnectedApps);
        yield put(setErrorScreenMessage(error, false, result.errorMessages));
        yield put(setScreenData(['isRoleMappingReady'], true));
    }
}

export function* updateRoleMappings(id, connectedApps, screenKey, isExistingUser) {
    let result;
    const errors = [];
    const failedAppsInfo = [];
    const filteredConnectedApps = filter(
        connectedApps,
        ({ applicationRoles }) => applicationRoles && !isEmpty(applicationRoles)
    );

    const allConnectedApps = getScreenData(Store().getState().screen, ['connectedApps'], screenKey)?.toJS() ?? [];
    const updatedData = getScreenUpdated(Store().getState().screen, [], screenKey)?.toJS() ?? [];
    const connectedAppsUpdatedData = get(updatedData, 'connectedApps') || {};
    const connectedAppsUpdatedResourcesData = get(updatedData, 'roleDetails.connectedApps') || {};
    const updatedConnectedAppsIdsForRoles = Object.keys(connectedAppsUpdatedData);
    const updatedConnectedAppsIdsForResources = Object.keys(connectedAppsUpdatedResourcesData);
    const updatedConnectedAppsIds = [
        ...new Set([...updatedConnectedAppsIdsForRoles, ...updatedConnectedAppsIdsForResources]),
    ];
    const connectedAppsAllIDs = filteredConnectedApps.map(item => item.connectedAppId);
    const itemsToDelete = updatedConnectedAppsIds.filter(element => !connectedAppsAllIDs.includes(element));

    if (!isEmpty(filteredConnectedApps)) {
        const calls = map(filteredConnectedApps, app => {
            const payload = { ...app, connectedAppRoles: app.applicationRoles };
            delete payload.connectedAppId;
            delete payload.applicationRoles;
            return call(
                PUT,
                `/iambff/v1/identity-roles/${id}/connected-apps/${app.connectedAppId}/connected-app-mappings`,
                getHeaders(),
                payload
            );
        });
        const addRoleResult = yield all(calls);
        if (!isEmpty(addRoleResult)) {
            const errorString = [];
            forEach(addRoleResult, (roleResult, index) => {
                if (!roleResult.isSuccess) {
                    const itemConnectedAppId = get(filteredConnectedApps[index], 'connectedAppId');
                    const appName = allConnectedApps.find(item => itemConnectedAppId === item?.connectedAppId);
                    const appDisplayName =
                        appName?.applicationBaseName ||
                        appName?.instanceFriendlyName ||
                        appName?.productName ||
                        appName?.connectedAppId ||
                        '';
                    errorString.push(`${get(roleResult, 'error')} ${appDisplayName}`);
                    failedAppsInfo.push(errorString);
                }
                if (!isEmpty(errorString)) {
                    errors.push({
                        screenMessage: get(roleResult, 'errorMessages'),
                        errorMessages: errorString,
                    });
                }
            });
        }

        if (size(itemsToDelete)) {
            for (let i = 0; i < size(itemsToDelete); i += 1) {
                result = yield call(
                    DELETE,
                    `/iambff/v1/identity-roles/${id}/connected-apps/${itemsToDelete[i]}/connected-app-mappings`,
                    getHeaders()
                );
                if (!result.isSuccess) {
                    const appName = allConnectedApps.find(item => itemsToDelete[i] === item?.connectedAppId);
                    const errorString = `${get(result, 'error')} ${appName?.applicationBaseName}`;
                    failedAppsInfo.push(errorString);
                    errors.push({
                        screenMessage: errorString,
                        errorMessages: get(result, 'errorMessages'),
                    });
                }
            }
        }

        const partialFailedApps = [];
        if (result && result.isSuccess) {
            const connectedAppSaveResults = get(result, 'response.connectedAppSaveResults');
            if (!isEmpty(connectedAppSaveResults)) {
                forEach(connectedAppSaveResults, ({ applicationName, isSuccessful }) => {
                    if (!isSuccessful) {
                        const connectedAppsState = getScreenData(
                            Store().getState().screen,
                            ['connectedApps'],
                            screenKey
                        );
                        const storedConnectedApps = connectedAppsState ? connectedAppsState.toJS() : {};
                        const connectedApp = find(
                            storedConnectedApps,
                            ({ connectedAppId }) => connectedAppId === applicationName
                        );

                        if (connectedApp) {
                            partialFailedApps.push(connectedApp.applicationBaseName);
                        }
                    }
                });
            }
        }

        if (!isEmpty(partialFailedApps)) {
            errors.push({
                screenMessage: formatMessage(Locale.addRoleAppAssignmentFailed, {
                    appNames: partialFailedApps.toString(),
                }),
                errorMessages: result.errorMessages,
            });
        }

        return {
            errors,
            isCompleteFail: result && (!result?.isSuccess || size(partialFailedApps) === size(connectedApps)),
        };
    }
    if (isExistingUser) {
        const connectedAppsPrepopulatedData =
            getScreenData(Store().getState().screen, ['connectedAppsPrepopulatedData'], screenKey)?.toJS() ?? [];
        let isAppRolesAlreadyPresent = false;
        if (!isEmpty(connectedAppsPrepopulatedData)) {
            isAppRolesAlreadyPresent = some(
                connectedAppsPrepopulatedData,
                app => app && !isEmpty(app.applicationRoles)
            );
        }
        if (isAppRolesAlreadyPresent) {
            const appRoleMappingResult = yield call(
                DELETE,
                `/iambff/v1/identity-roles/${id}/connected-app-mappings`,
                getHeaders()
            );
            if (!appRoleMappingResult.isSuccess) {
                errors.push({
                    screenMessage: get(appRoleMappingResult, 'error'),
                    errorMessages: get(appRoleMappingResult, 'errorMessages'),
                });
            }
            return {
                errors,
            };
        }
    }

    return { error: [] };
}

export function* deleteRoleWithAppRoleMapping(id, userCount) {
    if (userCount > 0) {
        const error = formatMessage(Locale.userCountErrorMsg);
        yield put(setErrorScreenMessage(error, false, []));
    } else {
        const result = yield call(DELETE, `/roles/${id}`, getHeaders());
        if (result.isSuccess) {
            yield call(loadRoles);
            yield put(setSuccessfulScreenMessage(formatMessage(Locale.roleDeleted), false, []));
        } else {
            const error = formatMessage(Locale.failedDeleteRole);
            yield put(setErrorScreenMessage(error, false, result.errorMessages));
        }
    }
    yield put(hideDialog(UMS_LOADING_DIALOG));
    yield call(autoHideMessages);
}

export function* deleteRole(id, userCount, isSilent = false) {
    if (userCount > 0) {
        const error = formatMessage(Locale.userCountErrorMsg);
        yield put(setErrorScreenMessage(error, false, []));
        yield call(autoHideMessages);
    } else {
        const result = yield call(DELETE, `/roles/${id}`, getHeaders());
        if (result.isSuccess) {
            if (!isSilent) {
                yield call(loadRoles);
                yield put(setSuccessfulScreenMessage(formatMessage(Locale.roleDeleted), false, []));
                yield call(autoHideMessages);
            }
        } else if (!isSilent) {
            const error = formatMessage(Locale.failedDeleteRole);
            yield put(setErrorScreenMessage(error, false, result.errorMessages));
            yield call(autoHideMessages);
        }
    }
}

export function* addRole(users, screenKey = ACTIVE_SCREEN_KEY) {
    const roleDetails = getScreenData(Store().getState().screen, ['roleDetails'], screenKey);
    const body = roleDetails ? roleDetails.toJS() : {};
    body.partnerId = getPartnerId();

    let errors = [];
    const result = yield call(POST, '/roles/', getHeaders(), body);

    if (result.isSuccess) {
        const roleDetailsResult = yield call(updateRoleUsers, result.response.id, users);
        errors = errors.concat(roleDetailsResult);
    } else {
        errors.push({
            screenMessage: formatMessage(Locale.failedCreateRole),
            errorMessages: result.errorMessages,
        });
    }

    if (errors.length > 0) {
        for (let i = 0; i < errors.length; i += 1) {
            yield put(setErrorScreenMessage(errors[i].screenMessage, false, errors[i].errorMessages, screenKey));
        }
        yield call(autoHideMessages, screenKey);
        if (result.isSuccess) {
            yield call(deleteRole, result.response.id);
            yield put(removeAllScreenMessages());
        }
    } else {
        yield put(hideDrawer(screenKey));
        yield call(loadRoles);
        yield put(setSuccessfulScreenMessage(formatMessage(Locale.successCreateRole), false, []));
        yield call(autoHideMessages);
    }
}

export function* editRole(id, roleDetailsUpdates, users, screenKey = ACTIVE_SCREEN_KEY) {
    let roleDetailsResult;
    let errors = [];
    if (roleDetailsUpdates) {
        roleDetailsResult = yield call(PATCH, `/roles/${id}`, getHeaders(), roleDetailsUpdates);
    }

    if (roleDetailsResult && !roleDetailsResult.isSuccess) {
        errors.push({
            screenMessage: formatMessage(Locale.failedUpdateRole),
            errorMessages: roleDetailsResult.errorMessages,
        });
    }

    const roleUsersResult = yield call(updateRoleUsers, id, users);
    errors = errors.concat(roleUsersResult);

    if (errors.length > 0) {
        for (let i = 0; i < errors.length; i += 1) {
            yield put(setErrorScreenMessage(errors[i].screenMessage, false, errors[i].errorMessages, screenKey));
        }
        yield call(autoHideMessages, screenKey);
    } else {
        yield put(hideDrawer(screenKey));
        yield call(loadRoles);
        yield put(removeScreenUpdated(['roleDetails'], screenKey));
        yield put(removeScreenUpdated(['connectedApps'], screenKey));
        yield put(setSuccessfulScreenMessage(formatMessage(Locale.roleUpdated), false, []));
    }
}

export function* addOrRemoveUsersFromRole(id, selectedUserIds, selectedOption, screenKey = ACTIVE_SCREEN_KEY) {
    const timeOut = yield fork(loader);
    let users = [];

    yield put(hideDialog(screenKey));
    const resultUsers = yield call(GET, `/roles/${id}/users`, getHeaders());
    if (resultUsers.isSuccess) {
        let errorRolesResult = [];
        users = resultUsers.response.map(user => user.id);
        switch (selectedOption) {
            case 'addRole':
                selectedUserIds.forEach(user => {
                    if (!users.includes(user)) {
                        users.push(user);
                    }
                });
                break;
            case 'removeRole':
                users = users.filter(user => !selectedUserIds.includes(user));
                break;
            default:
                break;
        }
        const rolesDetailsResult = yield call(updateRoleUsers, id, users);
        yield cancel(timeOut);
        errorRolesResult = errorRolesResult.concat(rolesDetailsResult);
        if (errorRolesResult.length > 0) {
            for (let i = 0; i < errorRolesResult.length; i += 1) {
                yield put(
                    setErrorScreenMessage(errorRolesResult[i].screenMessage, false, errorRolesResult[i].errorMessages)
                );
            }
        } else {
            yield put(setSuccessfulScreenMessage(formatMessage(Locale.RoleAssignmentUpdateSuccessful), false, []));
        }
    } else {
        const error = formatMessage(Locale.failedGetRoleUsers);
        yield put(setErrorScreenMessage(error, false, resultUsers.errorMessages));
    }
    yield call(autoHideMessages);
}

export function* bulkAddOrRemoveUsersFromRole(
    ids,
    selectedUserIds,
    selectedOption,
    screenKey = ACTIVE_SCREEN_KEY,
    originAccount = false
) {
    let users = [];
    yield put(hideDialog(screenKey));
    let roleIds = ids;
    if (ids && isImmutable(ids)) {
        roleIds = ids.toJS();
    }
    const getUsersUrls = map(roleIds, id => call(GET, `/roles/${id}/users`, getHeaders()));
    const resultUsersArray = yield all(getUsersUrls);

    const assignRolesUrls = [];
    const errorGetUsersForRole = [];
    const errorPutUsersForRole = [];

    for (let index = 0; index < resultUsersArray.length; index += 1) {
        const resultUsers = resultUsersArray[index];
        if (resultUsers.isSuccess) {
            users = resultUsers.response.map(user => user.id);
            switch (selectedOption) {
                case 'addRole':
                    for (let userIndex = 0; userIndex < selectedUserIds.length; userIndex += 1) {
                        const user = selectedUserIds[userIndex];
                        if (!users.includes(user)) {
                            users.push(user);
                        }
                    }
                    break;
                case 'removeRole':
                    users = users.filter(user => !selectedUserIds.includes(user));
                    users = {
                        userIDs: users,
                    };
                    break;
                default:
                    break;
            }
            assignRolesUrls.push(call(PUT, `/iambff/v1/identity-roles/${roleIds[index]}/users`, getHeaders(), users));
        } else {
            const error = formatMessage(Locale.failedGetRoleUsers);
            errorGetUsersForRole.push({
                screenMessage: error,
                errorMessages: resultUsers.errorMessages,
            });
        }
    }

    if (!isEmpty(assignRolesUrls)) {
        const rolesDetailsResults = yield all(assignRolesUrls);

        for (let responseIndex = 0; responseIndex < rolesDetailsResults.length; responseIndex += 1) {
            const rolesDetailsResult = rolesDetailsResults[responseIndex];
            if (rolesDetailsResult && !rolesDetailsResult.isSuccess) {
                errorPutUsersForRole.push({
                    screenMessage: formatMessage(Locale.failedToRemoveRole),
                    errorMessages: rolesDetailsResult.errorMessages,
                });
            }
        }
    }

    if (errorGetUsersForRole.length > 0 || errorPutUsersForRole.length > 0) {
        if (errorGetUsersForRole.length > 0) {
            // if we face error while retrieving list of users
            yield put(setErrorScreenMessage(formatMessage(Locale.failedGetRoleUsers), false, []));
        } else if (errorPutUsersForRole.length !== 0 && errorPutUsersForRole.length < assignRolesUrls.length) {
            // if we face error in some of the requests to update user to roles
            yield call(runSagaWithUMSLoader, reloadCurrentUsersPage, ACTIVE_SCREEN_KEY, true, originAccount);
            yield put(setWarningScreenMessage(formatMessage(Locale.partialFailureMessageBulkRoleRemoval), false, []));
        } else if (errorPutUsersForRole.length === assignRolesUrls.length) {
            // if we face error in all the requests
            yield put(setErrorScreenMessage(formatMessage(Locale.failedToRemoveRole), false, []));
        }
    } else {
        yield call(runSagaWithUMSLoader, reloadCurrentUsersPage, ACTIVE_SCREEN_KEY, false, originAccount);
        yield put(
            setSuccessfulScreenMessage(
                formatMessage(Locale.NewRoleAssignmentUpdateSuccessful, { rolesCount: size(selectedUserIds) }),
                false,
                []
            )
        );
        yield put(removeScreenData(['selectedIds']));
        yield put(removeScreenData(['selectedIdsForRoles']));
    }
    yield put(hideDialog(UMS_LOADING_DIALOG));
    yield call(autoHideMessages);
}

export function* showAdminRoleErrorMessage(screenKey = ACTIVE_SCREEN_KEY) {
    const error = formatMessage(Locale.adminRoleErrorMessage);
    yield put(setErrorScreenMessage(error, false, [], screenKey));
    yield call(autoHideMessages, screenKey);
}

export function getDefaultUniversityRole(screenKey = ACTIVE_SCREEN_KEY) {
    const roles = getScreenData(Store().getState().screen, ['roles'], screenKey);
    return roles?.find(role => role.get('name') === DEFAULT_ROLES.HomeBasicUser)?.toJS()?.id;
}

function isConnectedAppsSelected(connectedApps) {
    if (!connectedApps || connectedApps.length <= 0) {
        return false;
    }

    const isAllAppsEmpty = every(
        connectedApps,
        ({ applicationRoles }) => !applicationRoles || isEmpty(applicationRoles)
    );

    if (isAllAppsEmpty) {
        return false;
    }

    return every(connectedApps, ({ applicationRoles }) => {
        if (applicationRoles && !isEmpty(applicationRoles)) {
            return applicationRoles.every(
                ({ assignedResources, isResourcesEnabled }) =>
                    !isResourcesEnabled || (assignedResources && assignedResources.length > 0)
            );
        }
        return true;
    });
}

export function* addRoleWithUsersAndApps(connectedApps, screenKey = ACTIVE_SCREEN_KEY) {
    const roleDetails = getScreenData(Store().getState().screen, ['roleDetails'], screenKey);
    const body = roleDetails ? roleDetails.toJS() : {};
    delete body.connectedApps;

    let errors = [];
    const result = yield call(POST, '/roles/', getHeaders(), body);
    let isDeleteRole = false;

    if (result.isSuccess) {
        // Assign connected Apps to role
        if (isConnectedAppsSelected(connectedApps)) {
            const connectedAppsResult = yield call(
                updateRoleMappings,
                result.response.id,
                connectedApps,
                screenKey,
                false
            );
            errors = errors.concat(connectedAppsResult.errors);
            isDeleteRole = connectedAppsResult.isCompleteFail;
        }
    } else {
        errors.push({
            errorMessages: result.errorMessages,
        });
    }

    if (errors.length > 0) {
        yield put(hideDialog(UMS_LOADING_DIALOG));
        for (let i = 0; i < errors.length; i += 1) {
            yield put(
                setErrorScreenMessage(formatMessage(Locale.failedCreateRole), false, errors[i].errorMessages, screenKey)
            );
        }
        if (result.isSuccess && isDeleteRole) {
            yield call(deleteRole, result.response.id, 0, true);
            yield put(removeAllScreenMessages());
        }
        return { isError: true };
    }
    yield put(hideDialog(UMS_LOADING_DIALOG));
    yield put(setSuccessfulScreenMessage(formatMessage(Locale.successCreateRole), false, [], screenKey));
    return { isError: false, roleId: result?.response?.id };
}

export function* editRoleWithMappings(id, roleDetailsUpdates, connectedApps, screenKey = ACTIVE_SCREEN_KEY) {
    let roleDetailsResult;
    let errors = [];
    if (roleDetailsUpdates) {
        // This updates name and description of the role, not connected Apps
        roleDetailsResult = yield call(PATCH, `/roles/${id}`, getHeaders(), roleDetailsUpdates);
    }

    if (roleDetailsResult && !roleDetailsResult.isSuccess) {
        errors.push({
            screenMessage: formatMessage(Locale.failedCreateRole),
            errorMessages: roleDetailsResult.errorMessages,
        });
    }
    const connectedAppsResult = yield call(updateRoleMappings, id, connectedApps, screenKey, true);
    errors = connectedAppsResult?.errors ? errors.concat(connectedAppsResult.errors) : [];
    let isError = false;
    if (errors.length > 0) {
        yield put(hideDialog(UMS_LOADING_DIALOG));
        for (let i = 0; i < errors.length; i += 1) {
            yield put(
                setErrorScreenMessage(formatMessage(Locale.failedUpdateRole), false, errors[i].errorMessages, screenKey)
            );
        }
        isError = true;
    } else {
        yield call(loadConnectedAppsDetails, screenKey, id);
        yield call(getConnectedAppsMapping);
        yield call(runSagaWithUMSLoader, loadRoles);
        yield put(hideDialog(UMS_LOADING_DIALOG));
        yield put(removeScreenUpdated(['roleDetails'], screenKey));
        yield put(removeScreenUpdated(['connectedApps'], screenKey));
        yield put(setSuccessfulScreenMessage(formatMessage(Locale.roleUpdated), false, [], screenKey));
    }
    return { isError };
}

export function* showScreenErrorMessage(screenMessage, errorMessages, screenKey) {
    yield put(setErrorScreenMessage(screenMessage, false, errorMessages, screenKey));
    yield call(autoHideMessages, screenKey);
}

function* assignUsersToRoleUtil(id, users, screenKey) {
    const error = yield call(updateRoleUsers, id, users, formatMessage(Locale.addRoleUserAssignmentFailed));
    yield put(hideDialog(UMS_LOADING_DIALOG));
    if (!isEmpty(error)) {
        yield put(setErrorScreenMessage(error[0].screenMessage, false, error[0].errorMessages, screenKey));
        yield call(autoHideMessages, screenKey);
        return error;
    }
    return error;
}

export function* assignUsersToRole(id, users, screenKey) {
    // in case there are no users assigned but we will still show spinner for small duration to be consistent in the flow
    yield delay(100);

    // If EDIT_ROLE_DRAWER then we should not check users array because it can be empty array
    if (screenKey === EDIT_ROLE_DRAWER_ASSIGN_USERS_TAB) {
        const errors = yield call(assignUsersToRoleUtil, id, users, screenKey);
        if (isEmpty(errors)) {
            yield put(setScreenUpdated(['assignedUsers'], {}, EDIT_ROLE_DRAWER));
            yield put(setSuccessfulScreenMessage(formatMessage(Locale.assignedUsersToRole), false, [], screenKey));
            yield call(autoHideMessages, screenKey);
            yield call(loadRoles);
        }
        return { isError: !isEmpty(errors), assignedSuccessfully: true };
    }

    // otherwise for ADD_ROLE_DRAWER we should check whether users are assigned or not to save the API call
    if (!isEmpty(users)) {
        const errors = yield call(assignUsersToRoleUtil, id, users, screenKey);
        return { isError: !isEmpty(errors), assignedSuccessfully: true };
    }
    return { isError: false, assignedSuccessfully: !isEmpty(users) };
}

export function* deleteCreatedRoleAndMappings(roleId, screenKey = ACTIVE_SCREEN_KEY) {
    const deleteRoleResult = yield call(DELETE, `/roles/${roleId}`, getHeaders());
    let infoText = formatMessage(Locale.failedToDeleteIdentityRole);

    if (deleteRoleResult && deleteRoleResult.isSuccess) {
        infoText = formatMessage(Locale.roleDeleted);
        yield call(getConnectedAppsMapping);
        yield call(loadRoles);
    }

    yield put(hideDrawer(screenKey));
    yield put(hideDialog(UMS_LOADING_DIALOG));
    yield put(setInformationalScreenMessage(infoText, false, []));
    yield call(autoHideMessages);
}
